import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  FileDownload as TemplateIcon,
  Preview as PreviewIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import api from "../../services/api";

const ProductCsvImport = () => {
  const { storeId } = useParams();
  const fileInputRef = useRef(null);

  const [csvData, setCsvData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // ファイル選択
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ファイルタイプチェック
    if (!file.name.endsWith(".csv")) {
      setError("CSVファイルを選択してください");
      return;
    }

    setFileName(file.name);
    setError(null);
    setImportResult(null);
    setPreview(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvData(e.target.result);
    };
    reader.onerror = () => {
      setError("ファイルの読み込みに失敗しました");
    };
    reader.readAsText(file);
  };

  // プレビュー実行
  const handlePreview = async () => {
    if (!csvData) {
      setError("CSVファイルを選択してください");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/api/menu/${storeId}/products/preview`, {
        csvData,
        skipFirstRow: true,
      });

      setPreview(response.data);
      setPreviewDialogOpen(true);
    } catch (err) {
      console.error("プレビューエラー:", err);
      setError(err.response?.data?.message || "プレビューの生成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // インポート実行
  const handleImport = async () => {
    if (!csvData) {
      setError("CSVファイルを選択してください");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPreviewDialogOpen(false);

      const response = await api.post(`/api/menu/${storeId}/products/import`, {
        csvData,
        skipFirstRow: true,
        updateExisting,
      });

      setImportResult(response.data);
      setCsvData(null);
      setFileName("");
      setPreview(null);

      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("インポートエラー:", err);
      if (err.response?.data?.errors) {
        setError(`CSVにエラーがあります: ${err.response.data.errors.length}件`);
        setPreview({
          errors: err.response.data.errors,
          totalRows: err.response.data.validRows || 0,
          errorRows: err.response.data.errors.length,
        });
      } else {
        setError(err.response?.data?.message || "インポートに失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  // テンプレートダウンロード
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/menu/${storeId}/products/template`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("テンプレートダウンロードエラー:", err);
      setError("テンプレートのダウンロードに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // エクスポート
  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/menu/${storeId}/products/export`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `products_${storeId}_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("エクスポートエラー:", err);
      setError("エクスポートに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Typography variant="h4" gutterBottom>
        商品CSV一括登録
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        CSVファイルを使用して商品を一括登録・更新できます。
        まずテンプレートをダウンロードし、必要事項を入力してからアップロードしてください。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {importResult && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setImportResult(null)}
        >
          {importResult.message}
          <br />
          新規登録: {importResult.imported}件 / 更新: {importResult.updated}件 / スキップ: {importResult.skipped}件
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ダウンロード */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ダウンロード
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<TemplateIcon />}
                  onClick={handleDownloadTemplate}
                  disabled={loading}
                  fullWidth
                >
                  CSVテンプレートをダウンロード
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={loading}
                  fullWidth
                >
                  現在の商品をエクスポート
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                テンプレートにはサンプルデータが含まれています。
                エクスポートすると現在登録済みの商品をCSVでダウンロードできます。
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* アップロード */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                アップロード
              </Typography>

              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: "none" }}
                id="csv-file-input"
              />

              <label htmlFor="csv-file-input">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={loading}
                  fullWidth
                >
                  CSVファイルを選択
                </Button>
              </label>

              {fileName && (
                <Chip
                  label={fileName}
                  onDelete={() => {
                    setCsvData(null);
                    setFileName("");
                    setPreview(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  sx={{ mt: 2, width: "100%" }}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="同名商品を上書き更新する"
                sx={{ mt: 2 }}
              />

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  disabled={loading || !csvData}
                  sx={{ flex: 1 }}
                >
                  プレビュー
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <CheckIcon />
                  }
                  onClick={handleImport}
                  disabled={loading || !csvData}
                  sx={{ flex: 1 }}
                >
                  インポート
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* CSV仕様 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                CSV仕様
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>列名</TableCell>
                      <TableCell>必須</TableCell>
                      <TableCell>説明</TableCell>
                      <TableCell>例</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>商品名</TableCell>
                      <TableCell>
                        <Chip label="必須" color="error" size="small" />
                      </TableCell>
                      <TableCell>商品の名前</TableCell>
                      <TableCell>ビール</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>説明</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>商品の説明文</TableCell>
                      <TableCell>生ビール中ジョッキ</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>価格</TableCell>
                      <TableCell>
                        <Chip label="必須" color="error" size="small" />
                      </TableCell>
                      <TableCell>価格（数値）</TableCell>
                      <TableCell>500</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>カテゴリ</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>カテゴリ名</TableCell>
                      <TableCell>ドリンク</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>単位</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>数量の単位</TableCell>
                      <TableCell>杯</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>販売中</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>true または false</TableCell>
                      <TableCell>true</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>アレルゲン</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>カンマ区切りで複数指定</TableCell>
                      <TableCell>小麦,大豆</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>カロリー</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>カロリー値（数値）</TableCell>
                      <TableCell>150</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>表示順</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>表示順序（数値）</TableCell>
                      <TableCell>1</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>税区分</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>standard（標準）または reduced（軽減）</TableCell>
                      <TableCell>standard</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* プレビューダイアログ */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>インポートプレビュー</DialogTitle>
        <DialogContent>
          {preview && (
            <>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={`有効行数: ${preview.totalRows}`}
                  color="success"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={`エラー行数: ${preview.errorRows}`}
                  color={preview.errorRows > 0 ? "error" : "default"}
                />
              </Box>

              {preview.errors && preview.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">エラー一覧:</Typography>
                  {preview.errors.map((err, idx) => (
                    <Typography key={idx} variant="body2">
                      行 {err.rowNumber}: {err.errors.join(", ")}
                    </Typography>
                  ))}
                  {preview.hasMoreErrors && (
                    <Typography variant="body2" color="text.secondary">
                      ...他にもエラーがあります
                    </Typography>
                  )}
                </Alert>
              )}

              {preview.preview && preview.preview.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>商品名</TableCell>
                        <TableCell>価格</TableCell>
                        <TableCell>カテゴリ</TableCell>
                        <TableCell>販売中</TableCell>
                        <TableCell>税区分</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.preview.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>¥{row.price?.toLocaleString()}</TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell>
                            {row.available ? (
                              <CheckIcon color="success" fontSize="small" />
                            ) : (
                              <ErrorIcon color="error" fontSize="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {row.taxType === "reduced" ? "軽減" : "標準"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {preview.totalRows > 10 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  ※ 最初の10件のみ表示しています（全{preview.totalRows}件）
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>閉じる</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={loading || (preview?.errorRows || 0) > 0}
          >
            {loading ? <CircularProgress size={20} /> : "インポート実行"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductCsvImport;
